import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, HeartHandshake, Heart, Calendar, Users } from "lucide-react";
import SiteLayout from "../components/layout/SiteLayout";
import { api, resolveAssetUrl } from "../lib/api";
import { useSettings } from "../lib/settings";

function youtubeIdFromUrl(url = "") {
  const m =
    url.match(/youtu\.be\/([\w-]{6,})/) ||
    url.match(/youtube\.com\/watch\?v=([\w-]{6,})/) ||
    url.match(/youtube\.com\/embed\/([\w-]{6,})/);
  return m ? m[1] : null;
}

export default function HomePage() {
  const [sermons, setSermons] = useState([]);
  const [events, setEvents] = useState([]);
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/sermons").then((r) => setSermons(r.data.slice(0, 3))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const latest = sermons[0];
  const latestId = latest && youtubeIdFromUrl(latest.youtube_url);

  return (
    <SiteLayout>
      {/* HERO */}
      <section data-testid="hero-section" className="relative">
        <div className="absolute inset-0">
          <img src={resolveAssetUrl(settings.home_hero_image_url)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative container-x pt-32 pb-40 lg:pt-44 lg:pb-56 min-h-[80vh] flex flex-col justify-end">
          <div className="max-w-3xl text-[hsl(var(--linen))] fade-up">
            <div className="eyebrow text-[hsl(var(--linen))]/80 mb-6">{settings.tagline}</div>
            <h1 className="display text-5xl sm:text-6xl lg:text-7xl">
              {settings.hero_headline}
            </h1>
            <p className="mt-7 max-w-2xl text-base sm:text-lg text-[hsl(var(--linen))]/85 leading-relaxed">
              {settings.hero_subhead}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/visit"
                data-testid="hero-plan-visit"
                className="lift inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide"
              >
                Plan your visit <ArrowRight size={16} />
              </Link>
              <Link
                to="/sermons"
                data-testid="hero-watch-sermon"
                className="lift inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm border border-[hsl(var(--linen))]/40 hover:bg-[hsl(var(--linen))]/10 text-[hsl(var(--linen))] text-sm tracking-wide"
              >
                <PlayCircle size={16} /> Watch latest sermon
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TIMES STRIP */}
      <section data-testid="times-strip" className="border-y border-[hsl(var(--line))] bg-[hsl(var(--sand))]">
        <div className="container-x py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="eyebrow mb-2">Sunday Gatherings</div>
            <div className="display text-3xl">{settings.sunday_service_1} & {settings.sunday_service_2}</div>
          </div>
          <div>
            <div className="eyebrow mb-2">Address</div>
            <div className="text-[hsl(var(--ink))]">{settings.address_line1}</div>
            <div className="text-[hsl(var(--ink-muted))] text-sm">{settings.address_line2}</div>
          </div>
          <div>
            <div className="eyebrow mb-2">{settings.midweek_label}</div>
            <div className="text-[hsl(var(--ink))]">{settings.midweek_time}</div>
            <div className="text-[hsl(var(--ink-muted))] text-sm">{settings.midweek_location}</div>
          </div>
        </div>
      </section>

      {/* LATEST SERMON FEATURE */}
      <section data-testid="latest-sermon-section" className="container-x py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7">
            {latestId ? (
              <div className="youtube-frame rounded-sm overflow-hidden border border-[hsl(var(--line))]">
                <iframe
                  data-testid="latest-sermon-iframe"
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${latestId}`}
                  title={latest.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="youtube-frame bg-[hsl(var(--sand))] border border-[hsl(var(--line))]" />
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">{settings.home_sermon_eyebrow}</div>
            <h2 className="display text-4xl lg:text-5xl">{latest?.title || "Sermons coming soon"}</h2>
            <p className="mt-5 text-[hsl(var(--ink-muted))] leading-relaxed">
              {latest?.description}
            </p>
            <div className="mt-6 text-sm text-[hsl(var(--ink))]">
              {latest?.speaker} {latest?.scripture && <>· <span className="italic">{latest.scripture}</span></>}
            </div>
            <Link
              to="/sermons"
              data-testid="home-view-all-sermons"
              className="mt-10 inline-flex items-center gap-2 text-[hsl(var(--terracotta))] under-line text-sm tracking-wide"
            >
              {settings.home_sermon_cta} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* THREE WAYS */}
      <section data-testid="three-ways" className="bg-[hsl(var(--sand))] border-y border-[hsl(var(--line))]">
        <div className="container-x py-24">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">{settings.home_doors_eyebrow}</div>
            <h2 className="display text-4xl lg:text-5xl">{settings.home_doors_headline}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Heart, title: settings.home_tile_prayer_title, text: settings.home_tile_prayer_text, to: "/prayer", testid: "tile-prayer" },
              { icon: HeartHandshake, title: settings.home_tile_give_title, text: settings.home_tile_give_text, to: "/give", testid: "tile-give" },
              { icon: Users, title: settings.home_tile_about_title, text: settings.home_tile_about_text, to: "/about", testid: "tile-about" },
            ].map(({ icon: Icon, title, text, to, testid }) => (
              <Link
                key={testid}
                to={to}
                data-testid={testid}
                className="lift block bg-[hsl(var(--linen))] border border-[hsl(var(--line))] p-8 group"
              >
                <Icon size={22} className="text-[hsl(var(--terracotta))]" />
                <div className="display text-2xl mt-6">{title}</div>
                <p className="mt-3 text-sm text-[hsl(var(--ink-muted))] leading-relaxed">{text}</p>
                <div className="mt-8 text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink))] group-hover:text-[hsl(var(--terracotta))]">
                  Begin →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section data-testid="home-events" className="container-x py-24 lg:py-32">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-3">{settings.home_events_eyebrow}</div>
            <h2 className="display text-4xl lg:text-5xl">{settings.home_events_headline}</h2>
          </div>
          <Link to="/events" data-testid="home-all-events" className="under-line text-sm text-[hsl(var(--terracotta))]">
            {settings.home_events_cta}
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {events.map((e) => (
            <div
              key={e.id}
              data-testid={`home-event-${e.id}`}
              className="border border-[hsl(var(--line))] p-7 bg-[hsl(var(--linen))] flex flex-col"
            >
              <Calendar size={18} className="text-[hsl(var(--moss))]" />
              <div className="display text-2xl mt-5">{e.title}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-muted))] mt-4">
                {new Date(e.date).toLocaleDateString(undefined, { month: "long", day: "numeric" })} · {e.time}
              </div>
              <div className="text-sm text-[hsl(var(--ink-muted))] mt-3">{e.location}</div>
              <p className="text-sm text-[hsl(var(--ink-muted))] mt-5 leading-relaxed">{e.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMMUNITY BANNER */}
      <section data-testid="community-banner" className="relative">
        <div className="absolute inset-0">
          <img src={resolveAssetUrl(settings.home_community_image_url)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[hsl(var(--ink))]/55" />
        </div>
        <div className="relative container-x py-28 lg:py-40">
          <div className="max-w-3xl">
            <h2 className="display text-4xl lg:text-6xl text-[hsl(var(--linen))]">
              &ldquo;{settings.quote}&rdquo;
            </h2>
            <div className="mt-6 text-sm uppercase tracking-[0.22em] text-[hsl(var(--linen))]/70">
              {settings.quote_attribution}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
